import { Injectable, Signal, signal } from '@angular/core';
import { FormControl, ValidatorFn, Validators } from '@angular/forms';

export const PREFILL_LICENSE_PLATE_PARAM = 'prefillLicensePlate';
export const PREFILL_EMAIL_PARAM = 'prefillEmail';

// Same shape as LicensePlateService's validation; the interceptor re-validates as defense in depth.
const PLATE_VALIDATION = /^[A-Z0-9]{1,8}$/;

// Shared with the start form so capture-validation and form-validation can never drift.
export const START_EMAIL_VALIDATORS: ValidatorFn[] = [
  Validators.required,
  Validators.email,
  Validators.pattern(/\.[a-zA-Z]{2,}$/),
];

/**
 * Carries the deeplink prefill (SW-427.1) from the entry URL into the flow. Values live only
 * here (never in Session-/BookingState) and are one-shot: consuming clears them.
 */
@Injectable({ providedIn: 'root' })
export class EntryPrefillService {
  private readonly plate = signal<string | null>(null);
  private readonly email = signal<string | null>(null);

  public readonly prefillPlate: Signal<string | null> = this.plate.asReadonly();
  public readonly prefillEmail: Signal<string | null> = this.email.asReadonly();

  // Invalid input is dropped silently and never clears a previously captured value: the
  // strip-guard re-runs on every entry-route hop, usually without the params present.
  public capture(rawPlate: unknown, rawEmail: unknown): void {
    if (typeof rawPlate === 'string') {
      const normalized: string = rawPlate.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (PLATE_VALIDATION.test(normalized)) {
        this.plate.set(normalized);
      }
    }

    if (typeof rawEmail === 'string') {
      const trimmed: string = rawEmail.trim();
      if (new FormControl<string>(trimmed, START_EMAIL_VALIDATORS).valid) {
        this.email.set(trimmed);
      }
    }
  }

  public consumePlate(): string | null {
    const value: string | null = this.plate();
    this.plate.set(null);

    return value;
  }

  public consumeEmail(): string | null {
    const value: string | null = this.email();
    this.email.set(null);

    return value;
  }
}
